<script setup lang="ts">
import Button from "@/components/Reusables/Buttons/Button.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import { EmissionBudgetState } from "@/api/composables/useEmissionBudgets.composable";
import { UserInProjectType } from "@/features/BuildingProject";
import { GetProjectListQuery } from "@/graphql";
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";
import { getBuildingProjectUserList } from "../../../api/services/BuildingProject";
import { getUsersByTenantId } from "../../../api/services/Frontegg";
import { useAuthStateStore } from "../../../stores/Utils/auth/AuthState";
import User from "../../../types/auth/User";
import Modal from "../../Reusables/Modal/Modal.vue";
import ProjectCard from "@/pages/Projects/components/ProjectCard.vue";
import Divider from "@/components/Reusables/Divider/Divider.vue";

const props = defineProps<{
  emissionBudget?: EmissionBudgetState;
  project: GetProjectListQuery["project"][0];
}>();

const emit = defineEmits(["toggleRequestAccessModal"]);

const toggleView = () => {
  emit("toggleRequestAccessModal");
};

const authState = useAuthStateStore();
const { user } = storeToRefs(authState);
const usersAlreadyInProject = ref<Array<UserInProjectType>>([]);
const allUsers = ref<Array<User>>([]);
const users = ref<Array<User>>([]);
const tenantId = ref<string>();
const owners = ref<Array<User>>();
const usersLoaded = ref<boolean>(false);

onMounted(async () => {
  tenantId.value = user.value?.tenantId;
  if (tenantId.value === undefined) return;

  initializeUsers(tenantId.value);
});

const initializeUsers = async (tenantId: string) => {
  const response = await getUsersByTenantId(tenantId);
  allUsers.value = JSON.parse(JSON.stringify(response.items));
  users.value = response.items.sort((a: User, b: User) => (a.name > b.name ? 1 : -1));
  usersAlreadyInProject.value = await getBuildingProjectUserList(props.project.id);

  const ownerId = usersAlreadyInProject.value.find((u) => u.role === "admin")?.userId;
  owners.value = allUsers.value.filter((u) => u.id === ownerId);

  usersLoaded.value = true;
};

const emailSubject = () => encodeURIComponent(`Request for access to ${props.project.name} on RTLCA`);
const emailBody = () => encodeURIComponent(`Please add me to the project ${window.location.href}project/${props.project.id}`);
</script>

<template>
  <Modal
    heading="Request access to project"
    size="w-3/4 max-w-lg h-fit"
    @toggle-modal="toggleView">
    <div class="text-type-muted mx-1 my-5 flex flex-col gap-5">
      <!-- Project card preview -->
      <ProjectCard
        :emission-budget="emissionBudget"
        :has-access="false"
        :preview="true"
        :project="project" />

      <Divider spacing="none" />

      <!-- Access info -->
      <div class="border-element-stroke bg-surface rounded-16 border shadow-xs">
        <div class="flex items-center gap-2 px-4 py-4">
          <span class="text-text-color text-sm font-semibold">To obtain access, contact one of the project's admins:</span>
        </div>
        <Divider spacing="none" />
        <div class="p-4">
          <div
            v-if="usersLoaded"
            class="flex flex-col gap-3">
            <a
              v-for="owner in owners"
              :key="owner.id"
              :href="`mailto:${owner.email}?subject=${emailSubject()}&body=${emailBody()}`"
              class="group/owner border-element-stroke rounded-8 flex items-center gap-3 border p-2 pr-4 transition-all hover:border-slate-300 hover:bg-slate-100 hover:shadow-xs">
              <img
                class="h-10 w-10 rounded-full object-cover shadow-xs"
                :src="owner.profilePictureUrl"
                :alt="owner.name" />
              <div class="flex flex-col">
                <span class="text-text-color text-sm font-medium">{{ owner.name }}</span>
                <span class="text-type-muted text-xs underline">{{ owner.email }}</span>
              </div>
              <font-awesome-icon
                icon="fa-solid fa-envelope"
                class="ml-auto text-sm text-slate-400 transition-colors group-hover/owner:text-slate-600" />
            </a>
          </div>
          <LoadingSpinner
            v-else
            class="flex w-full justify-center py-4"
            size="lg"
            fill="primary" />
        </div>
      </div>
    </div>
    <div class="flex justify-end">
      <Button
        label="Close"
        color="primary"
        @click="emit('toggleRequestAccessModal')" />
    </div>
  </Modal>
</template>
